import type { AddressData } from '@choco-admin/types';
import { Button, Card, CardBody, CardHeader } from '@heroui/react';
import React, { useCallback, useState } from 'react';
import { Controller } from 'react-hook-form';

import {
  ChocoCheckbox,
  Input as FormInput,
  TextArea as FormTextArea,
  CountryFormField,
} from '../../../../components/FormComponents';
import { FormFooter } from '../../../../components/FormFooter';
import { AddressInput } from '../../../../components/GooglePlaces';

import { useBuyerForm } from './hooks/useBuyerForm';
import type { BuyerFormData, BuyerFormMode } from './types';

interface BuyerFormProps {
  /** Form mode (create or edit) */
  mode: BuyerFormMode;
  /** Initial form data for edit mode */
  initialData?: Partial<BuyerFormData>;
  /** Form submission handler */
  onSubmit: (data: BuyerFormData) => Promise<void>;
  /** Cancel handler */
  onCancel?: () => void;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Additional form title */
  title?: string;
}

export const BuyerForm: React.FC<BuyerFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  title,
}) => {
  const [showAllFormFields, setShowAllFormFields] = useState(mode === 'edit');
  const [businessNameInput, setBusinessNameInput] = useState('');

  const form = useBuyerForm({
    mode,
    initialData,
    onSubmit,
  });

  const handleAddressChange = useCallback(
    (addressData: AddressData) => {
      // Show all form fields when address is selected
      setShowAllFormFields(true);

      // Update form with address data
      form.setValue('fullAddress', addressData.fullAddress);
      form.setValue('addressSource', addressData.addressSource);
      form.setValue('name', addressData.name);
      form.setValue('addressCity', addressData.addressCity);
      form.setValue('addressCountry', addressData.addressCountry);
      form.setValue('addressLatitude', addressData.addressLatitude);
      form.setValue('addressLongitude', addressData.addressLongitude);
      form.setValue('addressUTCOffset', addressData.addressUTCOffset);
      form.setValue('addressGooglePlaceId', addressData.addressGooglePlaceId);
      form.setValue(
        'addressGoogleAddressId',
        addressData.addressGoogleAddressId
      );

      // Clear the business name input since the name goes to the actual name field
      setBusinessNameInput('');
    },
    [form]
  );

  const handleClearForm = useCallback(() => {
    form.reset();
    setShowAllFormFields(false);
    setBusinessNameInput('');
  }, [form]);

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card>
        {title && (
          <CardHeader>
            <h2 className="text-xl font-semibold">{title}</h2>
          </CardHeader>
        )}
        <CardBody>
          <form
            className="flex flex-col w-full space-y-8"
            onSubmit={form.handleSubmit}
          >
            <div className="space-y-2">
              <AddressInput
                id="business-search"
                label="Business Name (Search Google Places)"
                isRequired={true}
                placeholder="Search for business location..."
                value={businessNameInput}
                onAddressChange={handleAddressChange}
                onManualInput={(value: string) => {
                  setBusinessNameInput(value);
                  if (value.trim()) {
                    setShowAllFormFields(true);
                    form.setValue('name', value);
                  }
                }}
                isCustom={false}
              />
              {!showAllFormFields && (
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="bordered"
                    size="sm"
                    onPress={() => {
                      setShowAllFormFields(true);
                      handleAddressChange({
                        addressSource: 'custom',
                        fullAddress: '',
                        name: '',
                      });
                    }}
                  >
                    + Create custom buyer
                  </Button>
                </div>
              )}
            </div>

            {/* Rest of form - Only visible after address selection or in edit mode */}
            {showAllFormFields && (
              <>
                {/* Basic Information */}
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-foreground border-b border-divider pb-2">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Controller
                      name="name"
                      control={form.control}
                      render={({ field }) => (
                        <FormInput
                          id="buyer-name"
                          name={field.name}
                          label="Buyer Name"
                          value={field.value}
                          placeholder="Enter buyer name"
                          required={true}
                          isInvalid={!!form.errors.name}
                          errorMessage={form.errors.name?.message}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Controller
                      name="displayName"
                      control={form.control}
                      render={({ field }) => (
                        <FormInput
                          id="buyer-display-name"
                          name={field.name}
                          label="Display Name"
                          value={field.value || ''}
                          placeholder="Enter display name"
                          isInvalid={!!form.errors.displayName}
                          errorMessage={form.errors.displayName?.message}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-foreground border-b border-divider pb-2">
                    Address Information
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Controller
                        name="fullAddress"
                        control={form.control}
                        render={({ field }) => (
                          <FormTextArea
                            id="buyer-full-address"
                            name={field.name}
                            label="Full Address"
                            value={field.value || ''}
                            placeholder="Enter full address"
                            rows={3}
                            isInvalid={!!form.errors.fullAddress}
                            errorMessage={form.errors.fullAddress?.message}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                    <Controller
                      name="deliveryComment"
                      control={form.control}
                      render={({ field }) => (
                        <FormTextArea
                          id="buyer-delivery-comment"
                          name={field.name}
                          label="Delivery Comment"
                          value={field.value || ''}
                          placeholder="Additional delivery instructions"
                          rows={3}
                          isInvalid={!!form.errors.deliveryComment}
                          errorMessage={form.errors.deliveryComment?.message}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Controller
                        name="addressCity"
                        control={form.control}
                        render={({ field }) => (
                          <FormInput
                            id="buyer-city"
                            name={field.name}
                            label="City"
                            value={field.value || ''}
                            placeholder="Enter city"
                            isInvalid={!!form.errors.addressCity}
                            errorMessage={form.errors.addressCity?.message}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <CountryFormField
                        name="addressCountry"
                        control={form.control}
                        label="Country"
                        isRequired={false}
                      />
                    </div>
                  </div>
                </div>

                {/* Buyer Settings */}
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-foreground border-b border-divider pb-2">
                    Buyer Settings
                  </h3>
                  <div className="space-y-4">
                    <Controller
                      name="isTest"
                      control={form.control}
                      render={({ field }) => (
                        <ChocoCheckbox
                          isSelected={field.value}
                          onValueChange={field.onChange}
                          isDisabled={mode === 'edit'}
                          size="sm"
                          label="Test Buyer"
                          description={
                            mode === 'edit'
                              ? 'Cannot be changed after creation'
                              : 'Mark this buyer as a test account'
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Global form error display */}
                {form.errors.root && (
                  <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
                    <span className="text-danger text-small">
                      {form.errors.root.message}
                    </span>
                  </div>
                )}

                {/* Form Actions */}
                <div className="pt-6 border-t border-divider">
                  <div className="flex justify-between">
                    <div>
                      {mode === 'create' && (
                        <Button
                          type="button"
                          variant="bordered"
                          onPress={handleClearForm}
                          isDisabled={form.isSubmitting}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <FormFooter
                      onSave={form.handleSubmit}
                      onCancel={onCancel || (() => {})}
                      saveText={
                        mode === 'create' ? 'Create Buyer' : 'Save Changes'
                      }
                      cancelText="Cancel"
                      isSaveLoading={form.isSubmitting}
                      isSaveDisabled={mode === 'edit' && !form.isDirty}
                      isCancelDisabled={form.isSubmitting}
                    />
                  </div>
                </div>
              </>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  );
};
